import type { VisualRole } from "@eldrun/content";
import type { Mesh, Object3D, ShaderMaterial } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { unindexed } from "../geometry/unindexed.ts";
import { createSurfaceMaterial } from "../shaders/surface.ts";

export { applyRole } from "./roles.ts";
export { unindexed } from "../geometry/unindexed.ts";

/**
 * Malha monocromática com neon (F58): os papéis visuais do conteúdo (`VISUAL_ROLES`) viram os uniforms
 * do trecho `style` da superfície, e as geometrias perdem o índice (a malha baricêntrica sai de
 * `gl_VertexID % 3`). Nesta fatia só a vitrine `?debug=neon` usa; a partida entra na F59.
 */

/** Materiais por papel e cor: variantes de uniform do mesmo programa de superfície. */
export class NeonMaterials {
  private readonly cache = new Map<string, ShaderMaterial>();
  private readonly atmosphere: AtmosphereUniforms;
  constructor(atmosphere: AtmosphereUniforms) {
    this.atmosphere = atmosphere;
  }

  /** Sem cor, o material segue o acento do mundo (Fase 13, `setWorldAccent`). */
  get(role: VisualRole, accentHex?: string): ShaderMaterial {
    const key = `${role}:${accentHex ?? "mundo"}`;
    let material = this.cache.get(key);
    if (!material) {
      material = createSurfaceMaterial({ atmosphere: this.atmosphere, name: `neon-${key}`, vertexColors: true, outline: true, role, ...(accentHex !== undefined && { accent: accentHex }) });
      this.cache.set(key, material);
    }
    return material;
  }

  dispose(): void {
    for (const m of this.cache.values()) m.dispose();
    this.cache.clear();
  }
}

/** Troca geometria e material de cada malha sob `root`: as de nome `core` (a peça acesa) levam `coreRole`. */
export function neonize(root: Object3D, materials: NeonMaterials, role: VisualRole, accentHex: string, core?: { name: string; role: VisualRole }): void {
  root.traverse((o) => {
    const mesh = o as Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry = unindexed(mesh.geometry);
    mesh.material = materials.get(core && mesh.name === core.name ? core.role : role, accentHex);
  });
}
